import { evaluateScopedExpression } from "./expression-cache.js";
import { timerOutput } from "../behavior-timers/protocol.js";
import {
  dateDifferencePart,
  evaluateDateDifference,
} from "../blueprint/date-operators.js";
import {
  objectFactoryId,
  objectBasePort,
} from "../layout/scene-object-types.js";
import type {
  BehaviorNode,
  BinaryOperator,
  OperatorNode,
  Port,
  TernaryOperator,
  UnaryOperator,
} from "../blueprint/types.js";
import {
  constructObject,
  mapObjectValues,
} from "../blueprint/object-operators.js";
import { evaluateListAggregateGroupsBy } from "../blueprint/method.js";
import { coerceToBoolean } from "./helpers.js";
import { evaluateListSortBy } from "../blueprint/method.js";
import {
  buildBinaryOperatorOptions,
  buildUnaryOperatorOptions,
  evaluateBinaryOperator,
  evaluateFilter,
  evaluateFindByCondition,
  evaluateListFindByPathOutput,
  evaluateListFindOutput,
  evaluateListGetAtIndex,
  evaluateSort,
  evaluateTernaryOperator,
  evaluateTypedEquals,
  evaluateTypedNotEquals,
  evaluateUnaryObjectGet,
  evaluateUnaryOperator,
  getPortValueType,
  mergeCompiledDependencies,
  readComparatorItem,
  readCurrentItem,
  resolvePath,
  uniqueDependencies,
} from "./method.js";
import {
  FUNCTION_FLOW_IN_PORT_ID,
  FUNCTION_FLOW_OUT_PORT_ID,
} from "../blueprint/function-graph.js";
import type {
  CompiledValue,
  IndexedGraph,
  RuntimeContext,
  RuntimePortInputFrame,
  RuntimeVariableResolver,
  SceneCatalogDependencyIndex,
} from "./types.js";
import {
  dependencyForRuntimeVariable,
  hasRuntimeVariable,
  readRuntimeVariable,
} from "./runtimeVariables.js";

const LAYOUT_LIST_ROW_ITEM_SOURCE = "layoutListRowItem";
const LAYOUT_LIST_COMPARATOR_SOURCE = "layoutListComparator";

function mergeCatalogVariableDependencies(
  variableId: string | undefined,
  inputDeps: string[],
  catalogDeps?: SceneCatalogDependencyIndex,
): string[] {
  if (!variableId || !catalogDeps) {
    return uniqueDependencies(inputDeps);
  }

  return uniqueDependencies([
    ...inputDeps,
    ...(catalogDeps.variableDependenciesById[variableId] ?? []),
  ]);
}

function mergeCatalogFunctionDependencies(
  functionId: string | undefined,
  inputDeps: string[],
  catalogDeps?: SceneCatalogDependencyIndex,
): string[] {
  if (!functionId || !catalogDeps) {
    return uniqueDependencies(inputDeps);
  }

  return uniqueDependencies([
    ...inputDeps,
    ...(catalogDeps.functionDependenciesById[functionId] ?? []),
  ]);
}

function dependenciesForObjectSource(
  source: string,
  objectFieldPath?: string,
): string[] {
  const deps = [source];

  if (objectFieldPath) {
    deps.push(`${source}.${objectFieldPath}`);
  }

  if (source === LAYOUT_LIST_ROW_ITEM_SOURCE) {
    deps.push("rowIndex");
  }

  if (source === LAYOUT_LIST_COMPARATOR_SOURCE) {
    deps.push(
      "sortAIndex",
      "sortBIndex",
      "iterationItemA",
      "iterationItemB",
      "__layoutListBindingSource",
    );
  }

  return uniqueDependencies(deps);
}

function readLayoutListBindingRows(ctx: RuntimeContext): unknown[] | undefined {
  const sourceKey = ctx.__layoutListBindingSource;
  if (!sourceKey) {
    return undefined;
  }

  const source = ctx[sourceKey as keyof RuntimeContext];
  return Array.isArray(source) ? source : undefined;
}

/**
 * Context variables (`layoutListRowItem`, comparator A/B, row/sort indices).
 */
function compileContextVariableNode(
  node: BehaviorNode,
  portId: string,
): CompiledValue | null {
  const objectSource =
    "objectSource" in node && typeof node.objectSource === "string"
      ? node.objectSource
      : undefined;

  if (!objectSource) {
    return null;
  }

  const objectFieldPath =
    "objectFieldPath" in node && typeof node.objectFieldPath === "string"
      ? node.objectFieldPath
      : undefined;

  if (objectSource === LAYOUT_LIST_ROW_ITEM_SOURCE) {
    return {
      dependencies: dependenciesForObjectSource(
        LAYOUT_LIST_ROW_ITEM_SOURCE,
        objectFieldPath,
      ),
      evaluate: (ctx) => {
        const item = ctx.layoutListRowItem;
        if (item == null) {
          return undefined;
        }
        return objectFieldPath ? resolvePath(item, objectFieldPath) : item;
      },
    };
  }

  if (objectSource === LAYOUT_LIST_COMPARATOR_SOURCE) {
    return {
      dependencies: dependenciesForObjectSource(
        LAYOUT_LIST_COMPARATOR_SOURCE,
        objectFieldPath,
      ),
      evaluate: (ctx) => {
        const which: "A" | "B" = portId === "outputB" ? "B" : "A";
        const fromIteration = readComparatorItem(ctx, which, objectFieldPath);
        if (fromIteration !== undefined) {
          return fromIteration;
        }

        const rows = readLayoutListBindingRows(ctx);
        if (!rows) {
          return undefined;
        }

        const index = Math.max(
          0,
          Math.round(
            which === "B"
              ? Number(ctx.sortBIndex ?? 0)
              : Number(ctx.sortAIndex ?? 0),
          ),
        );
        const row = rows[index];
        if (row == null) {
          return undefined;
        }
        return objectFieldPath ? resolvePath(row, objectFieldPath) : row;
      },
    };
  }

  if (objectSource === "rowIndex") {
    return {
      dependencies: dependenciesForObjectSource("rowIndex"),
      evaluate: (ctx) => {
        const rowIndex = Number(ctx.rowIndex ?? 0);
        return Number.isFinite(rowIndex) ? Math.round(rowIndex) : 0;
      },
    };
  }

  if (objectSource === "sortAIndex") {
    return {
      dependencies: dependenciesForObjectSource("sortAIndex"),
      evaluate: (ctx) => {
        const sortAIndex = Number(ctx.sortAIndex ?? 0);
        return Number.isFinite(sortAIndex) ? Math.round(sortAIndex) : 0;
      },
    };
  }

  if (objectSource === "sortBIndex") {
    return {
      dependencies: dependenciesForObjectSource("sortBIndex"),
      evaluate: (ctx) => {
        const sortBIndex = Number(ctx.sortBIndex ?? 0);
        return Number.isFinite(sortBIndex) ? Math.round(sortBIndex) : 0;
      },
    };
  }

  if (objectSource === "barLoaderDisks") {
    return {
      dependencies: [],
      evaluate: () => node.ports.find((port) => port.id === portId)?.value,
    };
  }

  return null;
}

function compilePortDefaultValue(
  node: BehaviorNode,
  portId: string,
): CompiledValue {
  const port = node.ports.find((p: Port) => p.id === portId);

  return {
    dependencies: [],
    literal: { value: port?.value },
    evaluationScope: { sources: [], items: [], cost: 0 },
    evaluate: () => port?.value,
  };
}

type ValueCompilerContext = {
  compileInputValue(nodeId: string, portId: string): CompiledValue;
};

function compileUnaryOperator(
  node: OperatorNode,
  portId: string,
  compileInputValue: ValueCompilerContext["compileInputValue"],
): CompiledValue {
  const operator = node.operator;
  const unaryOptions = buildUnaryOperatorOptions(node);

  if (operator === "currentItem" || operator === "currentEntry") {
    const path = node.objectFieldPath;
    const list = compileInputValue(node.id, "input1");
    return {
      dependencies: uniqueDependencies([
        ...list.dependencies,
        ...(path
          ? ["iterationItem", `iterationItem.${path}`]
          : ["iterationItem"]),
      ]),
      evaluate: (ctx) => readCurrentItem(ctx, path, list.evaluate(ctx)),
    };
  }

  if (operator === "comparator") {
    const which = portId === "outputB" ? "B" : "A";
    const path = node.objectFieldPath;
    return {
      dependencies: path
        ? [
            "iterationItemA",
            "iterationItemB",
            `iterationItemA.${path}`,
            `iterationItemB.${path}`,
          ]
        : ["iterationItemA", "iterationItemB"],
      evaluate: (ctx) => readComparatorItem(ctx, which, path),
    };
  }

  const input1 = compileInputValue(node.id, "input1");

  if (operator === "get") {
    const path = node.objectFieldPath;
    const dataPath =
      input1.dataPath && path ? input1.dataPath + "." + path : undefined;
    return {
      dataPath,
      // A computed object's existing dependencies already cover its properties;
      // appending the property to every input invents unrelated dependency paths.
      dependencies: dataPath ? [dataPath] : input1.dependencies,
      evaluate: (ctx) => evaluateUnaryObjectGet(input1.evaluate(ctx), path),
    };
  }

  return {
    dependencies: input1.dependencies,
    evaluate: (ctx) =>
      evaluateUnaryOperator(
        operator as UnaryOperator,
        input1.evaluate(ctx),
        unaryOptions,
      ),
  };
}

function compileBinaryOperator(
  node: OperatorNode,
  portId: string,
  compileInputValue: ValueCompilerContext["compileInputValue"],
): CompiledValue {
  const operator = node.operator;
  const input1 = compileInputValue(node.id, "input1");
  const input2 = compileInputValue(node.id, "input2");
  const deps = mergeCompiledDependencies(input1, input2);
  if (operator === "dateDifference") {
    const part = dateDifferencePart(portId);
    return {
      dependencies: deps,
      evaluate: (ctx) =>
        part
          ? evaluateDateDifference(
              input1.evaluate(ctx),
              input2.evaluate(ctx),
              part,
            )
          : undefined,
    };
  }
  if (
    operator === "getByPath" &&
    input1.dataPath &&
    typeof input2.literal?.value === "string" &&
    input2.literal.value.trim()
  ) {
    const path = input2.literal.value.trim();
    return {
      dependencies: [input1.dataPath + "." + path],
      evaluate: (ctx) =>
        evaluateBinaryOperator(operator, input1.evaluate(ctx), path),
    };
  }
  if (operator === "mapItems" || operator === "mapValues") {
    return {
      dependencies: deps,
      evaluate: (ctx) => {
        const source = input1.evaluate(ctx);
        const transform = (item: unknown, index: number) =>
          input2.evaluate({
            ...ctx,
            iterationItem: item,
            iterationIndex: index,
            __iterationScopes: [
              ...(ctx.__iterationScopes ?? []),
              { list: source, item },
            ],
          });
        return operator === "mapItems"
          ? (Array.isArray(source) ? source : []).map(transform)
          : mapObjectValues(source, (value, key, index) =>
              transform({ key, value }, index),
            );
      },
    };
  }
  const input1ValueType = getPortValueType(node, "input1");

  if (operator === "aggregateGroups") {
    return {
      dependencies: uniqueDependencies([
        ...deps,
        "iterationItem",
        "iterationIndex",
      ]),
      evaluate: (ctx) =>
        evaluateListAggregateGroupsBy(input1.evaluate(ctx), (item, itemIndex) =>
          input2.evaluate({
            ...ctx,
            iterationItem: item,
            iterationIndex: itemIndex,
          }),
        ),
    };
  }

  if (operator === "findByCondition") {
    const wantsIndex = portId === "outputIndex";
    return {
      dependencies: uniqueDependencies([
        ...deps,
        "iterationItem",
        "iterationIndex",
      ]),
      evaluate: (ctx) =>
        evaluateFindByCondition(
          input1.evaluate(ctx),
          (scopedCtx) => input2.evaluate(scopedCtx),
          wantsIndex,
          ctx,
        ),
    };
  }

  if (operator === "filter") {
    return {
      dependencies: uniqueDependencies([
        ...deps,
        "iterationItem",
        "iterationIndex",
      ]),
      evaluate: (ctx) =>
        evaluateFilter(
          input1.evaluate(ctx),
          (scopedCtx) => input2.evaluate(scopedCtx),
          ctx,
        ),
    };
  }

  if (operator === "sortBy") {
    return {
      dependencies: uniqueDependencies([
        ...deps,
        "iterationItem",
        "iterationIndex",
      ]),
      evaluate: (ctx) => {
        const list = input1.evaluate(ctx);
        return evaluateListSortBy(list, (item, index) =>
          input2.evaluate({
            ...ctx,
            iterationItem: item,
            iterationIndex: index,
            __iterationScopes: [
              ...(ctx.__iterationScopes ?? []),
              { list, item },
            ],
          }),
        );
      },
    };
  }

  if (operator === "sort") {
    return {
      dependencies: uniqueDependencies([
        ...deps,
        "iterationItemA",
        "iterationItemB",
        "iterationIndex",
        "sortAIndex",
        "sortBIndex",
      ]),
      evaluate: (ctx) =>
        evaluateSort(
          input1.evaluate(ctx),
          (scopedCtx) => input2.evaluate(scopedCtx),
          ctx,
        ),
    };
  }

  if (operator === "equals") {
    return {
      dependencies: deps,
      evaluate: (ctx) =>
        evaluateTypedEquals(
          input1.evaluate(ctx),
          input2.evaluate(ctx),
          input1ValueType,
        ),
    };
  }

  if (operator === "notEquals") {
    return {
      dependencies: deps,
      evaluate: (ctx) =>
        evaluateTypedNotEquals(
          input1.evaluate(ctx),
          input2.evaluate(ctx),
          input1ValueType,
        ),
    };
  }

  if (operator === "get") {
    return {
      dependencies: deps,
      evaluate: (ctx) =>
        evaluateListGetAtIndex(input1.evaluate(ctx), input2.evaluate(ctx)),
    };
  }

  if (operator === "find") {
    const wantsIndex = portId === "outputIndex";
    const options = buildBinaryOperatorOptions(
      node,
      wantsIndex ? "index" : "item",
    );
    return {
      dependencies: deps,
      evaluate: (ctx) =>
        evaluateListFindOutput(
          input1.evaluate(ctx),
          input2.evaluate(ctx),
          options.objectFieldPath,
          wantsIndex,
        ),
    };
  }

  const options = buildBinaryOperatorOptions(node);
  return {
    dependencies: deps,
    evaluate: (ctx) =>
      evaluateBinaryOperator(
        operator as BinaryOperator,
        input1.evaluate(ctx),
        input2.evaluate(ctx),
        options,
      ),
  };
}

function compileTernaryOperator(
  node: OperatorNode,
  portId: string,
  compileInputValue: ValueCompilerContext["compileInputValue"],
): CompiledValue {
  const operator = node.operator;
  const input1 = compileInputValue(node.id, "input1");
  const input2 = compileInputValue(node.id, "input2");
  const input3 = compileInputValue(node.id, "input3");
  const deps = mergeCompiledDependencies(input1, input2, input3);

  if (operator === "ifElse") {
    return {
      dependencies: deps,
      evaluate: (ctx) =>
        coerceToBoolean(input3.evaluate(ctx))
          ? input1.evaluate(ctx)
          : input2.evaluate(ctx),
    };
  }

  if (operator === "findByPath") {
    const wantsIndex = portId === "outputIndex";
    return {
      dependencies: deps,
      evaluate: (ctx) =>
        evaluateListFindByPathOutput(
          input1.evaluate(ctx),
          input2.evaluate(ctx),
          input3.evaluate(ctx),
          wantsIndex,
        ),
    };
  }

  return {
    dependencies: deps,
    evaluate: (ctx) =>
      evaluateTernaryOperator(
        operator as TernaryOperator,
        input1.evaluate(ctx),
        input2.evaluate(ctx),
        input3.evaluate(ctx),
      ),
  };
}

function compileOperatorNode(
  node: OperatorNode,
  portId: string,
  compileInputValue: ValueCompilerContext["compileInputValue"],
  catalogDeps?: SceneCatalogDependencyIndex,
): CompiledValue {
  if (node.operator === "readTimer") {
    const reference = compileInputValue(node.id, "input1");
    return {
      dependencies: uniqueDependencies([
        ...reference.dependencies,
        "behaviorTimers",
      ]),
      evaluate: (ctx) =>
        timerOutput(ctx.behaviorTimers, reference.evaluate(ctx), portId),
    };
  }
  if (node.operator === "constructObject") {
    const inputs = node.ports
      .filter((p) => p.direction === "input")
      .map((port) => ({ port, value: compileInputValue(node.id, port.id) }));
    const factoryId = objectFactoryId(node.objectTypeId ?? "");
    const inheritedFactoryIds = node.objectDefinition
      ? inputs
          .filter(
            ({ port }) =>
              port.objectTypeId &&
              !port.objectTypeId.startsWith("source:") &&
              (port.id === "base" || port.id.startsWith("extension:")),
          )
          .map(({ port }) => objectFactoryId(port.objectTypeId!))
      : [];
    return {
      dependencies: uniqueDependencies([
        ...mergeCompiledDependencies(...inputs.map((input) => input.value)),
        ...(node.objectDefinition ? inheritedFactoryIds : [factoryId]).flatMap(
          (id) => catalogDeps?.variableDependenciesById[id] ?? [],
        ),
      ]),
      evaluate: (ctx) => {
        const args = Object.fromEntries(
          inputs.map(({ port, value }) => [port.id, value.evaluate(ctx)]),
        );
        if (!node.objectDefinition && ctx.__variables?.[factoryId]) {
          return ctx.__variables[factoryId](ctx, {
            byPortId: args,
            byLabel: {},
          });
        }
        const supplied = node.objectDefinition
          ? (ctx.__variableInputFrameStack?.at(-1)?.byPortId ?? {})
          : {};
        let base: unknown = {};
        for (const { port } of inputs) {
          if (port.id !== "base" && !port.id.startsWith("extension:")) continue;
          let value = supplied[port.id] ?? args[port.id];
          if (node.objectDefinition && port.objectTypeId) {
            if (port.objectTypeId.startsWith("source:"))
              value ??= ctx[port.objectTypeId.slice(7) as keyof RuntimeContext];
            else {
              const inherited =
                ctx.__variables?.[objectFactoryId(port.objectTypeId)];
              if (inherited)
                value = inherited(ctx, {
                  byPortId: { base: value },
                  byLabel: {},
                });
            }
          }
          base = constructObject(
            base,
            Object.entries(
              value != null &&
                typeof value === "object" &&
                !Array.isArray(value)
                ? value
                : {},
            ),
          );
        }
        return constructObject(
          base,
          inputs
            .filter(({ port }) => port.id.startsWith("field:"))
            .map(({ port }) => [
              port.id.slice(6),
              supplied[port.id] !== undefined
                ? supplied[port.id]
                : args[port.id],
            ]),
        );
      },
    };
  }
  switch (node.mode) {
    case "unary":
      return compileUnaryOperator(node, portId, compileInputValue);
    case "binary":
      return compileBinaryOperator(node, portId, compileInputValue);
    case "ternary":
      return compileTernaryOperator(node, portId, compileInputValue);
    default:
      throw new Error(
        `Unsupported operator mode: ${(node as OperatorNode).mode}`,
      );
  }
}

function readInputFramePort(
  frame: RuntimePortInputFrame | undefined,
  node: BehaviorNode,
  portId: string,
): unknown {
  if (!frame) {
    return undefined;
  }

  if (portId in frame.byPortId) {
    return frame.byPortId[portId];
  }

  const label = node.ports.find((port) => port.id === portId)?.label;
  if (label && label in frame.byLabel) {
    return frame.byLabel[label];
  }

  return undefined;
}

function compileFunctionCallNode(
  node: BehaviorNode,
  portId: string,
  compileInputValue: ValueCompilerContext["compileInputValue"],
  catalogDeps?: SceneCatalogDependencyIndex,
): CompiledValue {
  const functionId =
    "functionId" in node && typeof node.functionId === "string"
      ? node.functionId
      : undefined;

  if (!functionId) {
    return {
      dependencies: [],
      evaluate: () => undefined,
    };
  }

  const inputPorts = node.ports.filter(
    (port) =>
      port.direction === "input" &&
      port.valueType !== "flow" &&
      port.id !== FUNCTION_FLOW_IN_PORT_ID &&
      port.id !== FUNCTION_FLOW_OUT_PORT_ID,
  );

  const compiledInputs = inputPorts.map((port) => ({
    portId: port.id,
    label: port.label,
    value: compileInputValue(node.id, port.id),
  }));

  return {
    dependencies: mergeCatalogFunctionDependencies(
      functionId,
      mergeCompiledDependencies(...compiledInputs.map((input) => input.value)),
      catalogDeps,
    ),
    evaluate: (ctx) => {
      const functionRuntime = ctx.__functions?.[functionId];
      if (!functionRuntime) {
        return undefined;
      }

      const byPortId: Record<string, unknown> = {};
      const byLabel: Record<string, unknown> = {};

      for (const input of compiledInputs) {
        const value = input.value.evaluate(ctx);
        byPortId[input.portId] = value;

        if (input.label) {
          byLabel[input.label] = value;
        }
      }

      return functionRuntime.evaluateOutput(ctx, { byPortId, byLabel }, portId);
    },
  };
}

function compileVariableRefNode(
  node: BehaviorNode,
  compileInputValue: ValueCompilerContext["compileInputValue"],
  catalogDeps?: SceneCatalogDependencyIndex,
): CompiledValue {
  const variableId =
    "variableId" in node && typeof node.variableId === "string"
      ? node.variableId
      : undefined;
  if (!variableId) {
    return {
      dependencies: [],
      evaluate: () => undefined,
    };
  }

  const inputPorts = node.ports.filter((port) => port.direction === "input");

  const compiledInputs = inputPorts.map((port) => ({
    portId: port.id,
    label: port.label,
    value: compileInputValue(node.id, port.id),
  }));

  return {
    dependencies: mergeCatalogVariableDependencies(
      variableId,
      uniqueDependencies([
        dependencyForRuntimeVariable(variableId),
        ...mergeCompiledDependencies(
          ...compiledInputs.map((input) => input.value),
        ),
      ]),
      catalogDeps,
    ),

    evaluate: (ctx) => {
      if (hasRuntimeVariable(ctx, variableId)) {
        return readRuntimeVariable(ctx, variableId);
      }

      const variableRuntime = ctx.__variables?.[variableId];

      const byPortId: Record<string, unknown> = {};
      const byLabel: Record<string, unknown> = {};

      for (const input of compiledInputs) {
        const value = input.value.evaluate(ctx);
        byPortId[input.portId] = value;

        if (input.label) {
          byLabel[input.label] = value;
        }
      }

      if (typeof variableRuntime === "function") {
        return variableRuntime(ctx, {
          byPortId,
          byLabel,
        } satisfies Parameters<RuntimeVariableResolver>[1]);
      }

      return variableRuntime;
    },
  };
}

// Only expressions with explicit, pure inputs may be shared across iterations.
// Function/factory frames keep their original per-context cache.
const SCOPED_OPERATORS = new Set([
  "get",
  "getByPath",
  "castToNumber",
  "castToText",
  "equals",
  "notEquals",
  "greaterThan",
  "greaterThanOrEqual",
  "lessThan",
  "lessThanOrEqual",
  "and",
  "or",
  "not",
  "getYear",
  "add",
  "subtract",
  "multiply",
  "divide",
  "ifElse",
  "findByCondition",
  "filter",
  "sort",
  "sortBy",
  "isNotEmpty",
  "isEmpty",
  "concat",
  "map",
  "join",
  "length",
  "mapItems",
]);

function operatorEvaluationScope(
  node: OperatorNode,
  compileInput: ValueCompilerContext["compileInputValue"],
): CompiledValue["evaluationScope"] {
  if (node.operator === "comparator")
    return {
      sources: ["iterationItemA", "iterationItemB"],
      items: [],
      cost: 1,
    };
  if (node.operator === "currentItem" || node.operator === "currentEntry") {
    const list = compileInput(node.id, "input1");
    if (
      !list.evaluationScope ||
      (list.literal && list.literal.value === undefined)
    )
      return;
    return {
      ...list.evaluationScope,
      items: [...new Set([...list.evaluationScope.items, list])],
      cost: 1,
    };
  }
  if (!SCOPED_OPERATORS.has(node.operator)) return;
  const inputs = node.ports
    .filter((port) => port.direction === "input" && port.valueType !== "flow")
    .map((port) => ({
      portId: port.id,
      value: compileInput(node.id, port.id),
    }));
  if (!inputs.every((input) => input.value.evaluationScope)) return;
  const scopes = inputs.map((input) => ({
    ...input.value.evaluationScope!,
    portId: input.portId,
  }));
  const bindsItem = [
    "filter",
    "findByCondition",
    "sortBy",
    "mapItems",
  ].includes(node.operator);
  const list = bindsItem ? compileInput(node.id, "input1") : undefined;
  return {
    sources: [
      ...new Set(
        scopes.flatMap((scope) =>
          scope.sources.filter(
            (source) =>
              !(
                node.operator === "sort" &&
                scope.portId === "input2" &&
                ["iterationItemA", "iterationItemB"].includes(source)
              ),
          ),
        ),
      ),
    ],
    items: [
      ...new Set(
        scopes.flatMap((scope) =>
          scope.items.filter(
            (item) => !(scope.portId === "input2" && item === list),
          ),
        ),
      ),
    ],
    cost: Math.min(100, 1 + scopes.reduce((sum, scope) => sum + scope.cost, 0)),
    query: bindsItem || node.operator === "sort",
  };
}

export function createValueCompiler(
  indexed: IndexedGraph,
  catalogDeps?: SceneCatalogDependencyIndex,
) {
  const cache = new Map<string, CompiledValue>();

  function compileInputValue(nodeId: string, portId: string): CompiledValue {
    const connection = indexed.inputConnectionByNodeAndPort.get(
      `${nodeId}:${portId}`,
    );

    if (!connection) {
      const node = indexed.nodesById.get(nodeId);
      if (!node) {
        throw new Error(`Node not found: ${nodeId}`);
      }
      return compilePortDefaultValue(node, portId);
    }

    return compileNodeOutput(connection.fromNodeId, connection.fromPortId);
  }

  function compileNodeOutput(nodeId: string, portId: string): CompiledValue {
    const cacheKey = `${nodeId}:${portId}`;
    const cached = cache.get(cacheKey);
    if (cached) return cached;

    const node = indexed.nodesById.get(nodeId);
    if (!node) {
      throw new Error(`Node not found: ${nodeId}`);
    }

    let compiled: CompiledValue;

    if (node.type === "variable") {
      if (node.valueType !== "variableRef") {
        const contextVariable = compileContextVariableNode(node, portId);
        if (contextVariable) {
          compiled = contextVariable;
        } else {
          const source =
            "objectSource" in node && typeof node.objectSource === "string"
              ? node.objectSource
              : undefined;
          const objectFieldPath =
            "objectFieldPath" in node &&
            typeof node.objectFieldPath === "string"
              ? node.objectFieldPath
              : undefined;

          const extensionPort = objectBasePort(source);
          if (extensionPort) {
            const typeId =
              node.ports.find((p) => p.id === portId)?.objectTypeId ??
              node.ports.find((p) => p.direction === "output" && p.objectTypeId)
                ?.objectTypeId;
            const nativeSource = typeId?.startsWith("source:")
              ? typeId.slice(7)
              : undefined;
            compiled = {
              dependencies: nativeSource
                ? [nativeSource]
                : (catalogDeps?.variableDependenciesById[
                    objectFactoryId(typeId ?? "")
                  ] ?? []),
              evaluate: (ctx) => {
                let value =
                  ctx.__variableInputFrameStack?.at(-1)?.byPortId[
                    extensionPort
                  ] ??
                  (nativeSource
                    ? ctx[nativeSource as keyof RuntimeContext]
                    : undefined);
                if (typeId && !nativeSource) {
                  const factory = ctx.__variables?.[objectFactoryId(typeId)];
                  if (factory)
                    value = factory(ctx, {
                      byPortId: { base: value },
                      byLabel: {},
                    });
                }
                return objectFieldPath
                  ? resolvePath(value, objectFieldPath)
                  : value;
              },
            };
          } else
            compiled = {
              dataPath: source
                ? source + (objectFieldPath ? "." + objectFieldPath : "")
                : undefined,
              ...(!source
                ? {
                    literal: {
                      value: node.ports.find((port) => port.id === portId)
                        ?.value,
                    },
                  }
                : {}),
              dependencies: source
                ? dependenciesForObjectSource(source, objectFieldPath)
                : [],
              evaluate: (ctx) => {
                if (source) {
                  const value = ctx[source as keyof RuntimeContext];
                  return objectFieldPath
                    ? resolvePath(value, objectFieldPath)
                    : value;
                }

                return node.ports.find((p) => p.id === portId)?.value;
              },
            };
        }
      } else {
        compiled = compileVariableRefNode(node, compileInputValue, catalogDeps);
      }
    } else if (node.type === "operator") {
      compiled = compileOperatorNode(
        node as OperatorNode,
        portId,
        compileInputValue,
        catalogDeps,
      );
    } else if (
      node.type === "control" &&
      "controlType" in node &&
      node.controlType === "variableBegin"
    ) {
      compiled = {
        dependencies: [],
        evaluate: (ctx) => {
          const frameStack = ctx.__variableInputFrameStack;
          const frame = frameStack?.[frameStack.length - 1];
          return readInputFramePort(frame, node, portId);
        },
      };
    } else if (
      node.type === "control" &&
      "controlType" in node &&
      node.controlType === "functionBegin"
    ) {
      compiled = {
        dependencies: [],
        evaluate: (ctx) => {
          const frameStack = ctx.__functionCallFrameStack;
          const frame = frameStack?.[frameStack.length - 1];
          return readInputFramePort(frame, node, portId);
        },
      };
    } else if (
      node.type === "control" &&
      "controlType" in node &&
      node.controlType === "repeat" &&
      portId === "loopIndex"
    ) {
      compiled = {
        dependencies: [],
        evaluate: (ctx) => {
          const loopStack = ctx.__flowLoopStack ?? [];
          for (let index = loopStack.length - 1; index >= 0; index -= 1) {
            const loop = loopStack[index];
            if (loop?.nodeId === node.id) {
              return loop.index;
            }
          }
          return undefined;
        },
      };
    } else if (
      node.type === "action" &&
      "kind" in node &&
      node.kind === "call"
    ) {
      compiled = compileFunctionCallNode(
        node,
        portId,
        compileInputValue,
        catalogDeps,
      );
    } else {
      throw new Error(`Unsupported node type: ${node.type}`);
    }

    if (node.type === "variable" && (compiled.dataPath || compiled.literal)) {
      const source = compiled.dataPath?.split(".")[0];
      if (
        !source ||
        !/^(iterationItem|iterationIndex|sortAIndex|sortBIndex)/.test(source)
      )
        compiled.evaluationScope = {
          sources: source ? [source] : [],
          items: [],
          cost: 0,
        };
    }
    if (node.type === "operator") {
      compiled.evaluationScope = operatorEvaluationScope(
        node as OperatorNode,
        compileInputValue,
      );
      const expression = compiled;
      const dependsOnIteration = expression.dependencies.some((dependency) =>
        /^(iterationItem(?:A|B)?|iterationIndex|sortAIndex|sortBIndex|__iterationScopes)(\.|$)/.test(
          dependency,
        ),
      );
      compiled = {
        ...expression,
        evaluate: (ctx) => {
          const cache = ctx.__expressionCache;
          if (!cache) return expression.evaluate(ctx);
          if (expression.evaluationScope)
            return evaluateScopedExpression(expression, ctx);
          // Athlete/category/organisation expressions do not change for each
          // record in a filter. Cache those once in the enclosing scope.
          let scope = ctx;
          if (!dependsOnIteration) {
            let parent = cache.iterationParentByContext?.get(scope);
            while (parent) {
              scope = parent;
              parent = cache.iterationParentByContext?.get(scope);
            }
          }
          let values = cache.byContext.get(scope);
          if (!values) {
            values = new Map();
            cache.byContext.set(scope, values);
          }
          if (values.has(expression)) return values.get(expression);
          const value = expression.evaluate(ctx);
          values.set(expression, value);
          return value;
        },
      };
    }
    cache.set(cacheKey, compiled);
    return compiled;
  }

  return {
    compileInputValue,
    compileNodeOutput,
  };
}
